import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CocktailIcon } from '@/components/Brand';
import { InstallButton } from '@/components/InstallButton';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Enter username and password.');
      return;
    }
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success('Welcome back.');
      navigate('/dashboard');
    } else {
      setError(res.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1683744129473-77f257db55fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black pointer-events-none" />
      <div className="grain-overlay" />

      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-12">
          <CocktailIcon className="text-[#D4AF37]" size={32} />
          <div>
            <div className="font-display text-2xl font-medium tracking-tight">kahrency</div>
            <div className="label-eyebrow">The bartenders' ledger</div>
          </div>
        </div>

        <h1 className="text-4xl sm:text-5xl font-display font-medium tracking-tighter mb-2">Welcome back.</h1>
        <p className="text-slate-400 mb-10">Sign in to continue.</p>

        <form onSubmit={submit} className="space-y-6" data-testid="login-form">
          <div>
            <Label className="label-eyebrow mb-2 block">Username</Label>
            <Input
              data-testid="login-username-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="johndoe"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow">Password</Label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-xs text-[#D4AF37] hover:text-[#FDE047] font-medium"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
              {error}
            </div>
          )}
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full h-12 gold-btn"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <div className="mt-10 pt-6 border-t border-white/5 text-sm text-slate-400 flex items-center justify-between gap-4 flex-wrap">
          <div>
            New here?{' '}
            <Link to="/signup" data-testid="login-to-signup-link" className="text-[#D4AF37] hover:text-[#FDE047]">
              Create an account
            </Link>
          </div>
          <InstallButton />
        </div>
      </div>
    </div>
  );
}
